import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Form, Row, Col } from "react-bootstrap";
import DateField from "@components/FormsV2/DateField/DateField";
import Select2V2 from "@components/FormsV2/Select2/Select2V2";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import { ResolvedIntegration, Pair } from "../types/PsaProject";
import { usePsaProjectMutations } from "../api/usePsaProjectMutations";
import { usePsaAvailableProjects } from "../api/cascade/usePsaAvailableProjects";
import { usePsaDefaultCompany } from "../api/cascade/usePsaDefaultCompany";
import { usePsaProjectPhases } from "../api/cascade/usePsaProjectPhases";
import { usePsaProjectTickets } from "../api/cascade/usePsaProjectTickets";
import { useProjectServices } from "../api/cascade/useProjectServices";
import { useProjectPhases } from "../api/cascade/useProjectPhases";
import useStyles from "../styles/useStyles";
import CompanyTypeahead from "./CompanyTypeahead";
import MappingTable from "./MappingTable";

export interface ExportPsaFormHandle {
  save: () => void;
}

interface Props {
  integration: ResolvedIntegration;
  projectId: string;
  record?: any;
  readOnly?: boolean;
}

type Opt = { value: string; label: string };

const toOpts = (items?: { id?: string; name?: string }[]): Opt[] =>
  (items ?? []).map((i) => ({ value: String(i.id), label: i.name ?? "" }));
const capOpts = (options?: { value?: any; label?: string }[]): Opt[] =>
  (options ?? []).map((o) => ({
    value: String(o.value),
    label: o.label ?? "",
  }));
const toPairs = (map: Record<string, string>): Pair[] =>
  Object.entries(map)
    .filter(([, v]) => v)
    .map(([key, value]) => ({ key, value }));

// Saved mapping columns round-trip server-side as objects keyed by the
// ScopeStack-side id (`{ ssId: psaId }`) — see PsaProjectOptionsMapper. Some
// existing-project mappings may (per the mapper's `extract_mapping`) be
// nested under a `tickets` key; unwrap that defensively so read-only
// hydration is robust to either shape.
const toStrMap = (raw: any): Record<string, string> => {
  if (!raw || typeof raw !== "object") return {};
  const src =
    raw.tickets && typeof raw.tickets === "object" ? raw.tickets : raw;
  return Object.fromEntries(
    Object.entries(src).map(([k, v]) => [k, String(v)])
  );
};

const ExportPsaForm = forwardRef<ExportPsaFormHandle, Props>(
  ({ integration, projectId, record, readOnly }, ref) => {
    const styles = useStyles();
    const { create } = usePsaProjectMutations();
    const psaLabel =
      (
        {
          connectwise: "Connectwise",
          halopsa: "HaloPSA",
          autotask: "Autotask",
        } as Record<string, string>
      )[integration.service] || integration.service;
    const ref_ = integration.settings.reference ?? {};
    const cap = integration.settings.capabilities ?? {};

    const opts = record?.attributes?.["project-options"] ?? {};

    const boards = (ref_.boards?.items ?? []) as any[];
    const projectBoards = boards.filter((b) => b.type === "project");
    const boardOptions = toOpts(projectBoards);
    const hasBoard = boardOptions.length > 0;
    const serviceBoards = boards.filter((b) => b.type === "service");
    const serviceBoardOptions = toOpts(serviceBoards);
    const templates = (ref_.templates?.items ?? []) as any[];
    const templateOptions = toOpts(templates);
    const priorityOptions = toOpts(ref_.priorities?.items);
    const importOptions = capOpts(cap.import_options);
    const subtaskOptions = capOpts(cap.subtask_options);

    const findOpt = (list: Opt[], v: any) =>
      list.find((o) => o.value === String(v)) ?? null;

    // --- readOnly hydration: pre-select every control from the saved record.
    // `opts`/`record` are only ever non-empty when `readOnly` is also true
    // (Edit passes both together — New never passes a record), but we still
    // gate explicitly on `readOnly` so the create path can never be
    // accidentally pre-filled from stale data.
    const savedBoard = readOnly ? findOpt(boardOptions, opts.board) : null;
    // Mirrors the runtime `statusOptions` hasBoard-gating below: Connectwise
    // resolves the saved status through the saved board's nested statuses;
    // HaloPSA has no boards, so the saved status must resolve from the flat
    // `reference.statuses` list instead (otherwise it never pre-selects).
    const savedStatusOptions = hasBoard
      ? toOpts(projectBoards.find((b) => String(b.id) === opts.board)?.statuses)
      : toOpts(ref_.statuses?.items);
    const savedTemplate = readOnly
      ? findOpt(templateOptions, opts.template)
      : null;
    // The company field is a typeahead with no static option list, so there's
    // no name to resolve from a reference list — prefer the persisted
    // company_name (saved alongside the id on create); fall back to the raw
    // saved external id for older records that predate company_name.
    const savedCompany =
      readOnly && opts.company
        ? {
            value: String(opts.company),
            label: String(opts.company_name ?? opts.company),
          }
        : null;
    const savedProjectId = readOnly
      ? record?.attributes?.["project-id"] ?? opts.project_id
      : null;
    const savedAvailableProject = savedProjectId
      ? { value: String(savedProjectId), label: String(savedProjectId) }
      : null;

    const [startOn, setStartOn] = useState<string>(
      record?.attributes?.["start-on"] ?? ""
    );
    const [endOn, setEndOn] = useState<string>(
      record?.attributes?.["end-on"] ?? ""
    );
    const [importOpt, setImportOpt] = useState<Opt | null>(
      findOpt(importOptions, opts.import) ??
        importOptions.find((o) => o.value === "new") ??
        null
    );
    const [board, setBoard] = useState<Opt | null>(savedBoard);
    const [status, setStatus] = useState<Opt | null>(
      readOnly ? findOpt(savedStatusOptions, opts.status) : null
    );
    const [priority, setPriority] = useState<Opt | null>(
      readOnly ? findOpt(priorityOptions, opts.priority) : null
    );
    const [company, setCompany] = useState<Opt | null>(savedCompany);
    const [useTemplate, setUseTemplate] = useState<boolean>(!!opts.template);
    const [template, setTemplate] = useState<Opt | null>(savedTemplate);
    const [subtasks, setSubtasks] = useState<Opt | null>(
      readOnly ? findOpt(subtaskOptions, opts.subtasks) : null
    );
    const [governance, setGovernance] = useState<boolean>(
      opts.governance === "1"
    );
    const [availableProject, setAvailableProject] = useState<Opt | null>(
      savedAvailableProject
    );
    const [process, setProcess] = useState<string | null>(
      readOnly ? opts.process ?? null : null
    );
    const [taskMapping, setTaskMapping] = useState<Record<string, string>>(
      readOnly ? toStrMap(opts.task_mapping) : {}
    );
    const [phaseMapping, setPhaseMapping] = useState<Record<string, string>>(
      readOnly ? toStrMap(opts.phase_mapping) : {}
    );
    const [templateMapping, setTemplateMapping] = useState<
      Record<string, string>
    >(readOnly ? toStrMap(opts.project_template_tickets) : {});
    const [serviceBoard, setServiceBoard] = useState<Opt | null>(
      readOnly ? findOpt(serviceBoardOptions, opts.service_board) : null
    );
    const [error, setError] = useState("");
    const [showError, setShowError] = useState(false);

    const isNew = (importOpt?.value ?? "new") === "new";
    const isExisting = importOpt?.value === "existing";
    const isServiceTicket = importOpt?.value === "service_ticket";
    const selectedBoard = projectBoards.find(
      (b) => String(b.id) === board?.value
    );
    const statusOptions = hasBoard
      ? toOpts(selectedBoard?.statuses)
      : toOpts(ref_.statuses?.items);
    const selectedTemplate = templates.find(
      (t) => String(t.id) === template?.value
    );
    const templateTicketOptions = toOpts(selectedTemplate?.tickets);

    // Open a new push with the project's own client already selected — the
    // company is otherwise a typeahead that shows nothing until three
    // characters are typed. Skipped for readOnly, where `savedCompany` above
    // is the authority and the record's own selection must never be replaced
    // by whatever the client resolves to today.
    const { company: defaultCompany } = usePsaDefaultCompany(
      integration.id,
      projectId,
      { skip: !!readOnly }
    );
    // Keyed on the resolved id, not the hook's (freshly allocated) object, so
    // this seeds once per resolution rather than on every render; `current ??`
    // additionally protects a choice the user made while the request was in
    // flight.
    useEffect(() => {
      if (readOnly || !defaultCompany) return;
      setCompany((current) => current ?? defaultCompany);
    }, [readOnly, defaultCompany?.value]);

    const psaProjectId = availableProject?.value ?? "";
    const { projects: availableProjects } = usePsaAvailableProjects(
      integration.id,
      company?.value ?? "",
      hasBoard ? board?.value ?? "" : "",
      { requireBoard: hasBoard }
    );
    const { phases: psaPhases } = usePsaProjectPhases(
      integration.id,
      psaProjectId
    );
    const { tickets: psaTickets } = usePsaProjectTickets(
      integration.id,
      psaProjectId
    );
    const { services: ssServices } = useProjectServices(projectId);
    const { phases: ssPhases } = useProjectPhases(projectId);

    const clearExistingProjectDownstream = () => {
      setAvailableProject(null);
      setProcess(null);
      setTaskMapping({});
      setPhaseMapping({});
    };

    const onImportChange = (e: any) => {
      setImportOpt(e);
      setServiceBoard(null);
      clearExistingProjectDownstream();
    };

    const onBoardChange = (e: any) => {
      setBoard(e);
      setStatus(null);
      clearExistingProjectDownstream();
    };

    const onCompanyChange = (opt: Opt | null) => {
      setCompany(opt);
      clearExistingProjectDownstream();
    };

    const onAvailableProjectChange = (e: any) => {
      setAvailableProject(e);
      setProcess(null);
      setTaskMapping({});
      setPhaseMapping({});
    };

    const onProcessChange = (value: string) => {
      setProcess(value);
      if (value !== "task_as_ticket") setTaskMapping({});
      if (value !== "phase_as_ticket") setPhaseMapping({});
    };

    const onUseTemplateToggle = (value: boolean) => {
      setUseTemplate(value);
      setTemplateMapping({});
    };

    const onTemplateChange = (e: any) => {
      setTemplate(e);
      setTemplateMapping({});
    };

    const save = async () => {
      const projectOptions: any = {
        import: importOpt?.value,
        company: company?.value,
        company_name: company?.label,
        subtasks: subtasks?.value,
      };
      if (!isServiceTicket) {
        projectOptions.governance = governance ? "1" : "0";
      }
      if (isNew) {
        projectOptions.board = board?.value;
        projectOptions.status = status?.value;
        projectOptions.priority = priority?.value;
        if (useTemplate) {
          projectOptions.template = template?.value;
          projectOptions.project_template_tickets = toPairs(templateMapping);
        }
      }
      if (isExisting) {
        projectOptions.board = board?.value;
        projectOptions.project_id = availableProject?.value;
        projectOptions.process = process;
        if (process === "task_as_ticket") {
          projectOptions.task_mapping = toPairs(taskMapping);
        } else if (process === "phase_as_ticket") {
          projectOptions.phase_mapping = toPairs(phaseMapping);
        }
      }
      if (isServiceTicket) {
        projectOptions.service_board = serviceBoard?.value;
      }
      const attributes: any = {
        "start-on": startOn,
        "end-on": endOn,
        "project-options": projectOptions,
      };
      const body = {
        data: {
          type: "psa-projects",
          attributes,
          relationships: {
            "account-connection": {
              data: {
                type: "account-connections",
                id: integration.accountConnectionId,
              },
            },
          },
        },
      } as any;
      try {
        await create(projectId, body);
        // Full-page reload (not client-side navigate) so the record query
        // re-runs and reliably picks up the just-created record — matches
        // AutotaskForm's post-create navigation.
        window.location.assign(`/projects/${projectId}/psa_project/edit`);
      } catch (err: any) {
        setError(
          err?.data?.errors?.[0]?.detail ||
            "Something went wrong. Your changes could not be saved."
        );
        setShowError(true);
      }
    };

    useImperativeHandle(ref, () => ({ save }));

    return (
      <>
        {handleAlert(showError, error, setShowError, "warning", ToastAlert)}
        <Form>
          <Row>
            <Col>
              <Select2V2
                label="* Export Project As"
                options={importOptions}
                value={importOpt}
                onChange={onImportChange}
                isDisabled={readOnly}
              />
            </Col>
            <Col>
              <DateField
                id="exportPsaStartOn"
                label="* Estimated Start Date"
                value={startOn}
                onChange={(e) => setStartOn(e.target.value)}
                required
                disabled={readOnly}
              />
            </Col>
            <Col>
              <DateField
                id="exportPsaEndOn"
                label="* Estimated End Date"
                value={endOn}
                onChange={(e) => setEndOn(e.target.value)}
                required
                disabled={readOnly}
              />
            </Col>
          </Row>
          <CompanyTypeahead
            integrationId={integration.id}
            value={company}
            onChange={onCompanyChange}
            isDisabled={readOnly}
            label="* Company"
          />
          <Row>
            {(isNew || isExisting) && hasBoard && (
              <Col>
                <Select2V2
                  label="* Board"
                  options={boardOptions}
                  value={board}
                  onChange={onBoardChange}
                  isDisabled={readOnly}
                />
              </Col>
            )}
            {isNew && (
              <Col>
                <Select2V2
                  label="* Status"
                  options={statusOptions}
                  value={status}
                  onChange={(e: any) => setStatus(e)}
                  isDisabled={readOnly}
                />
              </Col>
            )}
            {isNew && priorityOptions.length > 0 && (
              <Col>
                <Select2V2
                  label="Priority"
                  options={priorityOptions}
                  value={priority}
                  onChange={(e: any) => setPriority(e)}
                  isDisabled={readOnly}
                />
              </Col>
            )}
          </Row>
          {isNew && (
            <>
              <div className={styles.formLabel} style={{ marginTop: 12 }}>
                {`Use a ${psaLabel} Project Template?`}
              </div>
              <div className={styles.toggleWrap}>
                <GenericToggle
                  initialValueOfIsOn={useTemplate}
                  onToggle={onUseTemplateToggle}
                  onText="Yes"
                  offText="No"
                  hasText={true}
                  disabled={readOnly}
                />
              </div>
              {useTemplate && (
                <Select2V2
                  label="* Template"
                  options={templateOptions}
                  value={template}
                  onChange={onTemplateChange}
                  isDisabled={readOnly}
                />
              )}
              {useTemplate && template && (
                <MappingTable
                  rows={ssServices}
                  targets={templateTicketOptions}
                  value={templateMapping}
                  onChange={(key, value) =>
                    setTemplateMapping((m) => ({ ...m, [key]: value }))
                  }
                  leftHeader="ScopeStack Service"
                  rightHeader={`${psaLabel} Ticket`}
                  readOnly={readOnly}
                />
              )}
            </>
          )}
          {isExisting && (
            <>
              <Select2V2
                label="* Available Project"
                options={availableProjects}
                value={
                  readOnly
                    ? findOpt(availableProjects, availableProject?.value) ??
                      availableProject
                    : availableProject
                }
                onChange={onAvailableProjectChange}
                isDisabled={readOnly}
              />
              {availableProject && (
                <>
                  <div className={styles.formLabel} style={{ marginTop: 12 }}>
                    How should ScopeStack Data be Imported?
                  </div>
                  <Form.Check
                    type="radio"
                    id="exportPsaProcessCurrent"
                    name="exportPsaProcess"
                    label="Use Current Process"
                    checked={process === "current_process"}
                    value="current_process"
                    onChange={() => onProcessChange("current_process")}
                    disabled={readOnly}
                  />
                  <Form.Check
                    type="radio"
                    id="exportPsaProcessTask"
                    name="exportPsaProcess"
                    label="Task as Ticket"
                    checked={process === "task_as_ticket"}
                    value="task_as_ticket"
                    onChange={() => onProcessChange("task_as_ticket")}
                    disabled={readOnly}
                  />
                  <Form.Check
                    type="radio"
                    id="exportPsaProcessPhase"
                    name="exportPsaProcess"
                    label="Phase as Ticket"
                    checked={process === "phase_as_ticket"}
                    value="phase_as_ticket"
                    onChange={() => onProcessChange("phase_as_ticket")}
                    disabled={readOnly}
                  />
                  {process === "phase_as_ticket" && (
                    <MappingTable
                      rows={ssPhases}
                      targets={psaPhases}
                      value={phaseMapping}
                      onChange={(key, value) =>
                        setPhaseMapping((m) => ({ ...m, [key]: value }))
                      }
                      leftHeader="ScopeStack Phase"
                      rightHeader={`${psaLabel} Phase`}
                      readOnly={readOnly}
                    />
                  )}
                  {process === "task_as_ticket" && (
                    <MappingTable
                      rows={ssServices}
                      targets={psaTickets}
                      value={taskMapping}
                      onChange={(key, value) =>
                        setTaskMapping((m) => ({ ...m, [key]: value }))
                      }
                      leftHeader="ScopeStack Service"
                      rightHeader={`${psaLabel} Ticket`}
                      readOnly={readOnly}
                    />
                  )}
                </>
              )}
            </>
          )}
          {isServiceTicket && (
            <Select2V2
              label="* Service Board"
              options={serviceBoardOptions}
              value={serviceBoard}
              onChange={(e: any) => setServiceBoard(e)}
              isDisabled={readOnly}
            />
          )}
          {!(isNew && useTemplate) && (
            <Select2V2
              label={`* How should Tasks be created in ${psaLabel}?`}
              options={subtaskOptions}
              value={subtasks}
              onChange={(e: any) => setSubtasks(e)}
              isDisabled={readOnly}
            />
          )}
          {!isServiceTicket && (
            <>
              <div className={styles.formLabel} style={{ marginTop: 12 }}>
                {`Create ${psaLabel} Tickets for ScopeStack Governance Items?`}
              </div>
              <div className={styles.toggleWrap}>
                <GenericToggle
                  initialValueOfIsOn={governance}
                  onToggle={setGovernance}
                  onText="Yes"
                  offText="No"
                  hasText={true}
                  disabled={readOnly}
                />
              </div>
            </>
          )}
        </Form>
      </>
    );
  }
);

ExportPsaForm.displayName = "ExportPsaForm";
export default ExportPsaForm;
